import React, { useState } from 'react';
import { Card, CardBody, Collapse } from 'reactstrap';
import './CollapsibleCard.scss';

/**
 * CollapsibleCard - A reusable expandable/collapsible card component
 *
 * Supports both controlled and uncontrolled modes:
 * - Uncontrolled: Use `defaultExpanded` prop (component manages its own state)
 * - Controlled: Use `expanded` and `onToggle` props (parent manages state)
 *
 * @param {string} title - The header title for the card
 * @param {boolean} defaultExpanded - Whether the card starts expanded (uncontrolled mode, default: true)
 * @param {boolean} expanded - Current expanded state (controlled mode)
 * @param {function} onToggle - Callback when toggle is clicked (controlled mode)
 * @param {React.ReactNode} children - The content to display inside the card
 * @param {React.ReactNode} headerContent - Additional content to render in the header (between title and toggle button)
 * @param {string} className - Additional CSS classes for the card
 * @param {string} headerClassName - Additional CSS classes for the header
 */
const CollapsibleCard = ({
  title,
  defaultExpanded = true,
  expanded,
  onToggle,
  children,
  headerContent = null,
  className = '',
  headerClassName = ''
}) => {
  // Determine if we're in controlled mode
  const isControlled = expanded !== undefined && onToggle !== undefined;

  // Internal state for uncontrolled mode
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Use controlled or uncontrolled state
  const isExpanded = isControlled ? expanded : internalExpanded;

  const toggleExpanded = () => {
    if (isControlled) {
      onToggle(!expanded);
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  return (
    <Card className={`collapsible-card no-bg-card ${className}`}>
      <div
        className={`collapsible-card-header ${headerClassName}`}
        onClick={toggleExpanded}
      >
        <h3 className="collapsible-card-title mb-0">{title}</h3>
        {headerContent && (
          <div className="collapsible-card-header-content" onClick={(e) => e.stopPropagation()}>
            {headerContent}
          </div>
        )}
        <button
          type="button"
          className={`collapsible-card-toggle ${isExpanded ? 'expanded' : 'collapsed'}`}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded();
          }}
          aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          aria-expanded={isExpanded}
        >
          <i className={`tim-icons ${isExpanded ? 'icon-minimal-up' : 'icon-minimal-down'}`}></i>
        </button>
      </div>
      <hr className="line-info collapsible-card-divider" />
      <Collapse isOpen={isExpanded}>
        <CardBody className="collapsible-card-body">
          {children}
        </CardBody>
      </Collapse>
    </Card>
  );
};

export default CollapsibleCard;
